import {v4 as uuid} from 'uuid';

/**
 * Identity and counters for the current Nova drawer session.
 *
 * Module state rather than React state: events fire from the drawer context, the stream hook and
 * the action card, which share no component above the drawer provider, and none of them should
 * re-render because a counter moved. The thread id is read through a getter registered by the
 * stream hook so it can never go stale between a thread rotation and the next event.
 *
 * The session is mirrored to sessionStorage, the same place the drawer keeps its own open state,
 * so a drawer that a reload restores open carries on as the same session rather than splitting
 * one open into two halves that never match up. sessionStorage also outlives things a session
 * must not survive, so the stored copy records who it belongs to and when its page was last seen:
 * a different viewer gets a fresh session, and the time between two page lives is not counted.
 */
export type NovaSessionSnapshot = {sessionDurationSeconds: number; messageCount: number; actionCount: number};

const STORAGE_KEY = 'nova_tracking_session';
const PROPOSED_KEY = 'nova_tracking_proposed_at';

type Stored = {
	drawerSessionId: string;
	openedAt: number;
	lastSeenAt: number;
	messageCount: number;
	actionCount: number;
	owner: string | null;
};

const noThread = (): null => null;

let drawerSessionId: string | null = null;
let openedAt = 0;
let messageCount = 0;
let actionCount = 0;
// The viewer the session belongs to, once the chat panel has resolved one.
let owner: string | null = null;
let threadIdSource: () => string | null | undefined = noThread;
// When each action was proposed, loaded on first use: a card proposed before a reload can be
// confirmed after it.
let proposedAt: Map<string, number> | null = null;
let pageHideHooked = false;

const persist = (): void => {
	try {
		if (!drawerSessionId || !openedAt) {
			sessionStorage.removeItem(STORAGE_KEY);
			return;
		}
		const stored: Stored = {drawerSessionId, openedAt, lastSeenAt: Date.now(), messageCount, actionCount, owner};
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
	} catch {
		// sessionStorage unavailable: the session still works for this page life.
	}
};

// Stamps lastSeenAt as the page goes away, so the next page life knows when this one ended.
const hookPageHide = (): void => {
	if (pageHideHooked || typeof window === 'undefined') return;
	pageHideHooked = true;
	window.addEventListener('pagehide', persist);
};

const readStored = (): Stored | null => {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<Stored>;
		if (typeof parsed.drawerSessionId !== 'string' || typeof parsed.openedAt !== 'number') return null;
		return {
			drawerSessionId: parsed.drawerSessionId,
			openedAt: parsed.openedAt,
			lastSeenAt: typeof parsed.lastSeenAt === 'number' ? parsed.lastSeenAt : parsed.openedAt,
			messageCount: Number(parsed.messageCount) || 0,
			actionCount: Number(parsed.actionCount) || 0,
			owner: typeof parsed.owner === 'string' ? parsed.owner : null,
		};
	} catch {
		return null;
	}
};

/**
 * Loads the stored session if none is in memory yet, and reports whether one is loaded afterwards.
 * Every accessor goes through this, so an event that fires before the drawer provider has resumed
 * continues the stored session instead of replacing it.
 */
const loadStored = (): boolean => {
	if (drawerSessionId) return true;
	const stored = readStored();
	if (!stored) return false;
	// Only the time up to the moment the previous page was last seen carries over: a reload, a tab
	// restored hours later and a return from another site in the same tab all leave a gap nobody
	// spent in the drawer.
	const elapsed = Math.max(0, stored.lastSeenAt - stored.openedAt);
	({drawerSessionId, messageCount, actionCount, owner} = stored);
	openedAt = Date.now() - elapsed;
	hookPageHide();
	persist();
	return true;
};

export const startNovaSession = (): string => {
	drawerSessionId = uuid();
	openedAt = Date.now();
	messageCount = 0;
	actionCount = 0;
	hookPageHide();
	persist();
	return drawerSessionId;
};

/** Continues the session stored by an earlier page life, or starts a new one if there is none. */
export const resumeNovaSession = (): string => (loadStored() ? (drawerSessionId as string) : startNovaSession());

export const endNovaSession = (): NovaSessionSnapshot => {
	loadStored();
	const snapshot = {
		sessionDurationSeconds: openedAt ? Math.round((Date.now() - openedAt) / 1000) : 0,
		messageCount,
		actionCount,
	};
	openedAt = 0;
	messageCount = 0;
	actionCount = 0;
	persist();
	return snapshot;
};

export const getNovaIdentity = (): {drawerSessionId: string; threadId: string | null} => {
	if (!drawerSessionId) resumeNovaSession();
	return {drawerSessionId: drawerSessionId as string, threadId: threadIdSource() ?? null};
};

/**
 * Registers where the live thread id is read from, and returns the unregister for the caller's
 * cleanup. The unregister only clears the source while it is still this one, so a newer
 * registration is never torn down by an older one's cleanup.
 */
export const setNovaThreadIdSource = (fn: () => string | null | undefined): (() => void) => {
	threadIdSource = fn;
	return () => {
		if (threadIdSource === fn) threadIdSource = noThread;
	};
};

/**
 * Ties the session to the viewer the chat panel has resolved (`viewer` is an opaque identity
 * string). sessionStorage outlives a sign-out and a sign-in as somebody else in the same tab, so a
 * live session stored for another viewer is not continued: this viewer gets a fresh one.
 */
export const claimNovaSession = (viewer: string): void => {
	const live = loadStored() && openedAt !== 0;
	const heldByAnother = live && owner !== null && owner !== viewer;
	owner = viewer;
	if (heldByAnother) {
		startNovaSession();
		return;
	}
	persist();
};

export const countNovaMessage = (): void => {
	loadStored();
	messageCount += 1;
	persist();
};

/**
 * Counts a proposed action. `sessionId`, when given, is the session the turn was sent in: an
 * action that lands after that session closed, or once another one has started, counts toward
 * neither.
 */
export const countNovaAction = (sessionId?: string): void => {
	loadStored();
	if (sessionId !== undefined && (sessionId !== drawerSessionId || !openedAt)) return;
	actionCount += 1;
	persist();
};

const proposals = (): Map<string, number> => {
	if (proposedAt) return proposedAt;
	const loaded = new Map<string, number>();
	try {
		const parsed: unknown = JSON.parse(sessionStorage.getItem(PROPOSED_KEY) ?? 'null');
		if (parsed && typeof parsed === 'object') {
			Object.entries(parsed).forEach(([id, at]) => {
				if (typeof at === 'number') loaded.set(id, at);
			});
		}
	} catch {
		// Unreadable or unavailable: start empty.
	}
	proposedAt = loaded;
	return loaded;
};

const persistProposals = (): void => {
	try {
		sessionStorage.setItem(PROPOSED_KEY, JSON.stringify(Object.fromEntries(proposals())));
	} catch {
		// sessionStorage unavailable: the timestamps still work for this page life.
	}
};

export const markNovaActionProposed = (actionId: string): void => {
	proposals().set(actionId, Date.now());
	persistProposals();
};

export const getNovaActionProposedAt = (actionId: string): number | undefined => proposals().get(actionId);

/** Drops an action's proposal time once the user has decided on it, so only open cards are kept. */
export const forgetNovaActionProposed = (actionId: string): void => {
	if (proposals().delete(actionId)) persistProposals();
};

/** Clears module state. `keepStorage` simulates a page reload, which keeps sessionStorage. */
export const resetNovaSessionForTests = ({keepStorage = false}: {keepStorage?: boolean} = {}): void => {
	drawerSessionId = null;
	openedAt = 0;
	messageCount = 0;
	actionCount = 0;
	owner = null;
	threadIdSource = noThread;
	proposedAt = null;
	if (!keepStorage) {
		try {
			sessionStorage.removeItem(STORAGE_KEY);
			sessionStorage.removeItem(PROPOSED_KEY);
		} catch {
			// sessionStorage unavailable
		}
	}
};
